import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { PromptConfirmation } from 'ui';
import { STATE } from 'app/constants';
import { AdministrationRoleEditState } from './types-store';
import { DefaultContainerProps } from 'app/infrastructure/redux/types';
import { containerFactory } from 'app/infrastructure/redux/index';
import { withSimpleForm } from 'app/infrastructure/simple-form/with-simple-form';
import { actionCreators } from './actions';
import { administrationEditTranslations } from 'app/area/administration/edit/constants';
import { defaultSerializeMappings } from 'app/infrastructure/simple-form/serialize';
import { serializeAccessData } from '../serialize';
import { FormValuesType, SimpleFormProps } from 'app/infrastructure/simple-form/types';
import { withPrompt } from 'app/components/prompt-unsaved/prompt-unsaved';
import { AdminPaper } from 'app/area/administration/edit/components/admin-paper';
import { SpinLoader } from 'app/components/spin-loader/index';
import { AdministrationRoleEditProps, RoleEditSectionType } from 'app/area/administration/edit/role/types';
import { PersonActionType } from 'app/area/administration/shared/types';
import { OverlayComponent } from 'app/components/overlay/overlay';
import { withOptions } from 'app/components/options-handler';
import { compareAccess } from 'app/area/administration/edit/compare-access';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { RoleEditForm } from 'app/area/administration/edit/role/role-edit-form';
import { metroLineConfiguration } from 'app/area/administration/edit/role/configuration';
import { RefObject } from 'react';
import { routeConstants } from 'app/routes/route-constants';
import { safeParam, safeNumericString } from 'app/infrastructure/data/sanitize';
import { roleEditFactory } from 'app/area/administration/edit/role/role-edit-factory';
import { fetchingOptions, validationToBooleanValues } from 'app/area/administration/shared/helpers';
import { RoleStickyHeader } from 'app/area/administration/edit/role/role-sticky-header';
import { getInvalidSection } from 'app/components/metro-line/helpers';
import { WithInvalidSection } from 'app/components/metro-line/types';
import { sfNames } from 'app/area/administration/edit/sf-names';
import { compareDefaultRole } from './compare-default-role';
import { scrollToTop } from 'app/infrastructure/platform/routing';

type RoleEditState = WithInvalidSection<RoleEditSectionType>;

export class RoleEdit extends React.Component<AdministrationRoleEditProps, RoleEditState> {
  public static statePath: string = STATE.administrationRoleEdit;

  public state: Readonly<RoleEditState> = {
    invalidSection: null
  };

  private detailsSectionRef: RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
  private accessToOperationSectionRef: RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
  private defaultValuesSectionRef: RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
  private accessToScreenSectionRef: RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
  private extendedAccessSectionRef: RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
  private userOccurenceSectionRef: RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
  private restrictedAccessRef: RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();

  public componentWillUnmount() {
    this.props.clearAllValues();
  }

  public componentDidMount() {
    const { id, onLoadRole, loadRoleAccess, loadExtraInfo, loadPersonOptions, loadDefaultRole, checkAccess, sharedState } = this.props;
    scrollToTop();

    if (!sharedState.access.adminPanel.role) {
      navigate.toStartupView();
    } else {
      if (isDefinedIncludingZero(id)) {
        onLoadRole();
        loadRoleAccess(id);
      } else {
        if (this.hasTextsLoaded()) {
          checkAccess(sharedState.access.adminPanel.role.create);
        }
      }
      loadDefaultRole(id);
      loadExtraInfo();
      loadPersonOptions();
    }
  }

  public componentDidUpdate(prevProps: AdministrationRoleEditProps) {
    const { id, texts, checkAccess, sharedState } = this.props;

    (prevProps.simpleForm.editing.validation !== this.props.simpleForm.editing.validation ||
      prevProps.validation !== this.props.validation) &&
      this.state.invalidSection === undefined && this.updateInvalidSection();

    this.clearServerValidation(prevProps.formValues);

    if (this.hasTextsLoaded() && (prevProps.texts !== texts || !!prevProps.params && prevProps.params.id !== id)) {
      if (isDefinedIncludingZero(id)) {
        checkAccess(sharedState.access.adminPanel.role.read);
      } else {
        checkAccess(sharedState.access.adminPanel.role.create);
      }
    }
  }

  private hasTextsLoaded = () => !!this.props.texts && !!this.props.texts.texts;

  private clearServerValidation = (prevFormValues: FormValuesType) => {
    const { formValues, validation, clearValidation } = this.props;
    const formIds = [sfNames.name, sfNames.personId];

    formIds.forEach(key => formValues[key] !== prevFormValues[key] && validation[key] && clearValidation(key));
  };

  private onSave = async () => {
    const { currentDefaultRole, markedAsDefaultRole, id: roleId, translation } = this.props;

    await this.props.onSave({ currentDefaultRole, markedAsDefaultRole, roleId, translation });
    this.updateInvalidSection();
  };

  private updateInvalidSection = () => {
    const { id, params: { actionType }, validation: serverValidation,
      sharedState: { access: { adminPanel: { restrictedAccessPanel } = {} } = {} } = {} } = this.props;

    const { simpleForm: {
      editing: { validation = {} } = {}
    } = {} } = this.props;

    const configuration = metroLineConfiguration(
      id,
      actionType,
      restrictedAccessPanel,
      this.detailsSectionRef,
      this.accessToOperationSectionRef,
      this.defaultValuesSectionRef,
      this.accessToScreenSectionRef,
      this.extendedAccessSectionRef,
      this.restrictedAccessRef,
      this.userOccurenceSectionRef,
      validationToBooleanValues({ ...validation, ...serverValidation })
    );

    const invalidSection = getInvalidSection(configuration);

    this.setState({ invalidSection });
  };

  public render() {
    const { invalidSection } = this.state;
    const { id, fetchingLoadRole, fetchingSaveRole, data, options, accessPerson, translation,
      simpleForm: {
        editing: { validation = {} } = {}
      } = {}, validation: serverValidation,
      params: { actionType }, section,
      sharedState: { access: { adminPanel: { restrictedAccessPanel } = {} } = {} } = {} } = this.props;

    const shouldRender = !fetchingLoadRole
      && ((!isDefinedIncludingZero(id) && !accessPerson) || (data && !!accessPerson));

    if (!shouldRender) {
      return <SpinLoader />;
    }

    const configuration = metroLineConfiguration(
      id,
      actionType,
      restrictedAccessPanel,
      this.detailsSectionRef,
      this.accessToOperationSectionRef,
      this.defaultValuesSectionRef,
      this.accessToScreenSectionRef,
      this.extendedAccessSectionRef,
      this.restrictedAccessRef,
      this.userOccurenceSectionRef,
      validationToBooleanValues({ ...validation, ...serverValidation })
    );

    const scrollViewUrl = getScrollViewUrl(actionType, id);
    const optionsFetching = !!fetchingOptions(options);

    return (<div>
      {optionsFetching && <SpinLoader />}
      <OverlayComponent
        displayOverlay={fetchingSaveRole}
        overlayText={translation.save_role_load_overlay_text} />
      {this.props.shouldDisplayModal
        ? <PromptConfirmation
          title={translation.cancel_changes}
          description={translation.are_you_sure}
          continueText={translation.undo_all}
          cancelText={translation.go_back}
          onContinue={this.props.continue}
          onCancel={this.props.cancel}
        />
        : ''}
      {<AdminPaper>
        <RoleStickyHeader {...this.props} onSave={this.onSave} optionsFetching={optionsFetching} />
        <RoleEditForm
          {...this.props}
          invalidSection={invalidSection}
          validation={{ ...validation, ...serverValidation }}
          configuration={configuration}
          scrollViewUrl={scrollViewUrl}
          initialSection={section as RoleEditSectionType}
          label={translation.role_administration_label}
        />
      </AdminPaper>}
    </div>);
  }
}

const getScrollViewUrl = (actionType: PersonActionType, id: number) => {
  return actionType && {
    [PersonActionType.add]: routeConstants.roleAddFor(PersonActionType.add),
    [PersonActionType.edit]: isDefinedIncludingZero(id) ? routeConstants.roleEditFor(id) : undefined,
    [PersonActionType.duplicate]: routeConstants.roleDuplicateFor(id)
  }[actionType];
};

const inEditMode = (data, accessPerson) => (accessPerson && accessPerson.edit) || !data;

const selectors = {
  id: (state: AdministrationRoleEditState, props: DefaultContainerProps) => safeNumericString(props.params.id),
  editMode: ({ data, accessPerson }: AdministrationRoleEditState, props: DefaultContainerProps) => inEditMode(data, accessPerson),
  shouldPrompt: ({ data, accessPerson, accessData, extraInfoCodes, markedAsDefaultRole, currentDefaultRole }: AdministrationRoleEditState,
    { params: { actionType } = {}, serialize, sharedState: { access: { adminPanel } } }: DefaultContainerProps & SimpleFormProps) =>
    inEditMode(data, accessPerson) &&
    (compareAccess({
      ...serialize(defaultSerializeMappings),
      ...serializeAccessData(accessData, actionType, extraInfoCodes, data, adminPanel)
    }, data) ||
      compareDefaultRole(markedAsDefaultRole, data?.id, currentDefaultRole)),
  section: (_: AdministrationRoleEditState, props: DefaultContainerProps) => safeParam(props.params.section)
};

const container = withSimpleForm(containerFactory(withPrompt(withOptions(roleEditFactory(RoleEdit))),
  actionCreators, selectors, administrationEditTranslations));

export { container as RoleEditView };
